/**
 * useMeetingRequests Hook
 *
 * Wraps useRequestMeetingMutation and useGetMyActivitiesQuery (filtered by MEETING type)
 * for the customer-facing meeting request feature.
 *
 * Requirements: Task 14 - Meeting Request Page
 */

import { useMemo, useCallback } from 'react';
import {
  useRequestMeetingMutation,
  useGetMyActivitiesQuery,
  type GetMyActivitiesQuery,
  type PaginationInput,
} from '@bloomcode/graphql';

// Type aliases for query results
type ActivityItem = GetMyActivitiesQuery['getMyActivities']['data'][number];
type PaginationInfo = GetMyActivitiesQuery['getMyActivities']['pagination'];

// ============================================================================
// PENDING MEETINGS HOOK
// ============================================================================

interface UsePendingMeetingsOptions {
  page?: number;
  limit?: number;
  skip?: boolean;
}

interface UsePendingMeetingsResult {
  meetings: ActivityItem[];
  pagination: PaginationInfo | null;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch pending meeting requests (activities with type=MEETING)
 * Filters client-side for isCompleted=false since the query returns all meetings
 */
export function usePendingMeetings(
  options: UsePendingMeetingsOptions = {}
): UsePendingMeetingsResult {
  const { page = 1, limit = 20, skip = false } = options;

  const pagination: PaginationInput = { page, limit };

  const {
    data,
    loading,
    error,
    refetch: refetchQuery,
  } = useGetMyActivitiesQuery({
    variables: {
      pagination,
      filter: { type: ['MEETING'] },
    },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Filter for pending (not completed) meetings
  const meetings: ActivityItem[] = useMemo(
    () => (data?.getMyActivities?.data ?? []).filter((a) => !a.isCompleted),
    [data]
  );

  const paginationInfo: PaginationInfo | null = useMemo(
    () => data?.getMyActivities?.pagination ?? null,
    [data]
  );

  const totalCount = meetings.length;

  const refetch = useCallback(() => {
    refetchQuery();
  }, [refetchQuery]);

  return {
    meetings,
    pagination: paginationInfo,
    totalCount,
    isLoading: loading,
    error: error ?? null,
    refetch,
  };
}

// ============================================================================
// REQUEST MEETING HOOK
// ============================================================================

interface RequestMeetingInput {
  preferredDate: string;
  topic: string;
  notes?: string;
}

interface UseRequestMeetingResult {
  requestMeeting: (input: RequestMeetingInput) => Promise<{
    success: boolean;
    message?: string;
  }>;
  isRequesting: boolean;
}

/**
 * Hook to request a meeting with the account representative
 */
export function useRequestMeeting(): UseRequestMeetingResult {
  const [requestMeetingMutation, { loading }] = useRequestMeetingMutation();

  const requestMeeting = useCallback(
    async (input: RequestMeetingInput) => {
      try {
        const result = await requestMeetingMutation({
          variables: {
            input: {
              preferredDate: input.preferredDate,
              topic: input.topic,
              notes: input.notes,
            },
          },
          refetchQueries: ['GetMyActivities'],
        });

        if (result.data?.requestMeeting?.success) {
          return { success: true, message: result.data.requestMeeting.message };
        }

        return {
          success: false,
          message: result.data?.requestMeeting?.message ?? 'Request failed',
        };
      } catch (err) {
        return {
          success: false,
          message: err instanceof Error ? err.message : 'Network error',
        };
      }
    },
    [requestMeetingMutation]
  );

  return {
    requestMeeting,
    isRequesting: loading,
  };
}

export type { ActivityItem as MeetingItem, RequestMeetingInput };
